import {
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  deleteSymptomReports,
  getSymptomCheckerDetails,
  getSymptomQuestion,
  getSymptomReports,
  postSymptomFileUpload,
  postSymptomQuestion,
  updateSymptomQuestion,
} from './api';
import {
  GetSymptomParams,
  SymptomCheckerDetailResponse,
  SymptomQuestionPayload,
  SymptomQuestionResponse,
} from './type';

export const useInfiniteGetSymptomReports = (
  profileId: string,
  initialPageParam: number
) => {
  return useInfiniteQuery({
    queryKey: ['inifinite-symptom-reports', profileId],
    queryFn: async ({ pageParam }) => {
      const response = await getSymptomReports({
        page_number: pageParam,
        page_size: 10,
      });

      return response;
    },
    initialPageParam,
    getNextPageParam: (lastPage, allPages) => {
      const allData = allPages.flatMap((page) => page.data);
      if (allData.length >= lastPage.count) return undefined;

      return allPages.length + 1;
    },
    select: ({ pages }) => pages.flatMap((page) => page.data),
    gcTime: 0,
    staleTime: 0,
  });
};

interface GetSymptomReportDetail extends UseQueryOptions {
  tokenId: string;
}
export const useGetSymptomReportDetail = (
  props: Omit<GetSymptomReportDetail, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['symptom-report-detail', props.tokenId],
    queryFn: async () => await getSymptomCheckerDetails(props.tokenId),
    enabled: !!props.tokenId,
    staleTime: Infinity,
    gcTime: Infinity,
    ...props,
  }) as UseQueryResult<SymptomCheckerDetailResponse[]>;
};

export const usePostSymptomQuestion = (
  args?: Omit<
    UseMutationOptions<
      SymptomQuestionResponse,
      Error,
      SymptomQuestionPayload,
      unknown
    >,
    'mutationFn'
  >
) => {
  return useMutation({
    mutationFn: postSymptomQuestion,
    ...args,
  });
};

export const usePostSymptomFileUpload = (
  args?: Omit<
    UseMutationOptions<
      { image_url: string; token_id: string }[],
      Error,
      FormData,
      unknown
    >,
    'mutationFn'
  >
) => {
  return useMutation({
    mutationFn: postSymptomFileUpload,
    ...args,
  });
};

export const useUpdateSymptomQuestion = (
  args?: Omit<
    UseMutationOptions<
      SymptomQuestionResponse,
      Error,
      SymptomQuestionPayload,
      unknown
    >,
    'mutationFn'
  >
) => {
  return useMutation({
    mutationFn: updateSymptomQuestion,
    ...args,
  });
};

export const useDeleteSymptomReports = (
  props?: Omit<
    UseMutationOptions<unknown, Error, string[], unknown>,
    'mutationFn'
  >
) => {
  return useMutation({
    mutationKey: ['delete-symptom-report'],
    mutationFn: deleteSymptomReports,
    ...props,
  });
};

interface useGetSymptomQuestionArgs extends UseQueryOptions {
  payload: GetSymptomParams & { profileId: string };
}
export const useGetSymptomQuestion = (
  args: Omit<useGetSymptomQuestionArgs, 'queryKey' | 'queryFn'>
) => {
  const type =
    'retrieve_type' in args.payload
      ? args.payload.retrieve_type
      : args.payload.type;

  return useQuery({
    queryKey: ['questionnaire-v3', type, args.payload.profileId],
    queryFn: () => getSymptomQuestion(args.payload),
    staleTime: 0,
    gcTime: 0,
    ...args,
  }) as UseQueryResult<SymptomQuestionResponse>;
};
